/* =====================================================================
   Shop filter configuration.

   The reference design shows a richer category list than the backend
   currently returns. `/sales/getCategories` today returns only:

       "Home & Garden ", "Fashion ", "Pet Supplies", "Packaging "

   So the design's category names are declared here as the display layer.
   Each entry lists the backend category name(s) it maps to. Entries with
   an empty `aliases` array are shown in the sidebar but stay disabled
   ("Soon") until the backend exposes a matching category — at that point
   just add the backend name to `aliases`; nothing else has to change.

   No API call, request shape, or response mapping is affected by this
   file: it only decides which backend category string a sidebar row
   sends into the existing product filter.
   ===================================================================== */

export const PRODUCTS_PER_PAGE = 20;

/* Normalise for comparison only — the value handed to the filter is
   always the untouched backend string (some names carry trailing
   spaces, e.g. "Home & Garden "). */
export const normalizeName = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/* Display categories, in the order shown in the reference design. */
export const CATEGORY_FILTERS = [
  {
    id: "home-living",
    label: "Home & Living",
    aliases: ["home & garden", "home and garden", "home & living"],
  },
  {
    // TODO: map once the backend splits "Home & Garden" into its own
    // outdoor/garden category.
    id: "garden-outdoor",
    label: "Garden & Outdoor",
    aliases: ["garden & outdoor", "garden and outdoor", "garden"],
  },
  {
    id: "fashion-accessories",
    label: "Fashion & Accessories",
    aliases: ["fashion", "fashion & accessories"],
  },
  {
    id: "pet-products",
    label: "Pet Products",
    aliases: ["pet supplies", "pet products", "pets"],
  },
  {
    id: "eco-packaging",
    label: "Eco Packaging",
    aliases: ["packaging", "eco packaging"],
  },
];

/*
 * Resolve the display list against whatever the categories endpoint
 * returned. Any backend category that no display entry claims is
 * appended as-is, so a newly created category shows up on its own.
 */
export const buildCategoryOptions = (backendCategories = []) => {
  const backend = (backendCategories || [])
    .map((item) => (typeof item === "string" ? item : item?.name))
    .filter(Boolean);

  const claimed = new Set();

  const mapped = CATEGORY_FILTERS.map((entry) => {
    const match = backend.find((name) => {
      const normalized = normalizeName(name);
      return (
        normalized === normalizeName(entry.label) ||
        entry.aliases.some((alias) => normalizeName(alias) === normalized)
      );
    });

    if (match) claimed.add(match);

    return {
      id: entry.id,
      label: entry.label,
      // The exact backend string, or null while the category does not exist.
      value: match ?? null,
    };
  });

  const extras = backend
    .filter((name) => !claimed.has(name))
    .map((name) => ({
      id: `api-${normalizeName(name).replace(/[^a-z0-9]+/g, "-")}`,
      label: String(name).trim(),
      value: name,
    }));

  return [...mapped, ...extras];
};

/*
 * Materials. `product.material` is free text on the backend
 * ("Coir Fiber & Wood", "Coconut Husk ", …), so each option carries the
 * substrings that identify it — the same substring matching the existing
 * material filter already used.
 */
export const MATERIAL_FILTERS = [
  { id: "clay", label: "Clay & Ceramic Products", match: ["clay"] },
  { id: "coir", label: "Coir Products", match: ["coir"] },
  { id: "coconut", label: "Coconut Products", match: ["coconut"] },
  { id: "wooden", label: "Wooden Products", match: ["wood"] },
  { id: "palmyra", label: "Palmyra Products", match: ["palmyra"] },
  { id: "cane", label: "Cane & Rattan Products", match: ["cane", "rattan"] },
  { id: "reed", label: "Reed & Rush Products", match: ["reed", "rush"] },
  {
    id: "fabric",
    label: "Fabric & Cotton Products",
    match: ["fabric", "cotton", "canvas", "thread"],
  },
  { id: "areca", label: "Areca Leaf Products", match: ["areca"] },
  { id: "brass", label: "Brass Products", match: ["brass"] },
  { id: "paper", label: "Paper & Recycled", match: ["paper"] },
];

/* Customer review buckets, as in the reference design. */
export const RATING_FILTERS = [
  { id: "all", label: "All", value: 0 },
  { id: "four-plus", label: "& Above", value: 4 },
];
